(() => {
  if (window.merchantBuyNowController) return;
  window.merchantBuyNowController = true;
  const states = new WeakMap();
  const active = new Set();
  let frame = 0;

  function cancel(state) {
    const request = state.request;
    state.request = null;
    if (request) {
      clearTimeout(request.timeout);
      request.controller.abort();
      active.delete(request);
    }
  }

  function context(host) {
    const button = host.querySelector('.merchant-buy-button');
    const scope = host.closest('.product-details') || host.closest('.shopify-section');
    const form = scope?.querySelector('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
    let state = states.get(host);
    if (!state) {
      state = { host, version: 0, pending: false, failed: false, request: null };
      states.set(host, state);
    }
    if ((state.button && state.button !== button) || (state.form && state.form !== form)) cancel(state);
    state.button = button;
    state.form = form;
    state.scope = scope;
    if (button && !state.label) state.label = host.dataset.label || button.textContent.trim();
    return state;
  }

  function message(state, text) {
    const element = state.host.querySelector('.merchant-buy-error');
    if (element && element.textContent !== text) element.textContent = text;
  }

  function sync(host) {
    const state = context(host);
    const { button, form } = state;
    if (!button) return state;
    const variant = form?.querySelector('[name="id"]');
    const add = form?.querySelector('button[name="add"], .add-to-cart-button');
    const available = Boolean(variant?.value && !variant.disabled && add && !add.disabled);
    const busy = Boolean(state.request);
    const disabled = busy || state.pending || state.failed || !available;
    const label = busy ? host.dataset.busy
      : state.pending ? host.dataset.updating
      : state.failed || !variant?.value || !add ? host.dataset.unavailable
      : !available ? host.dataset.soldout : state.label;
    if (button.disabled !== disabled) button.disabled = disabled;
    if (button.textContent !== label) button.textContent = label;
    if (busy) {
      if (button.getAttribute('aria-busy') !== 'true') button.setAttribute('aria-busy', 'true');
    } else if (button.hasAttribute('aria-busy')) button.removeAttribute('aria-busy');
    return state;
  }

  function syncAll() {
    frame = 0;
    for (const request of active) {
      if (!request.state.host.isConnected) cancel(request.state);
    }
    document.querySelectorAll('merchant-buy-now').forEach(sync);
  }

  function schedule() {
    if (!frame) frame = requestAnimationFrame(syncAll);
  }

  function restore() {
    for (const request of active) cancel(request.state);
    document.querySelectorAll('merchant-buy-now').forEach((host) => {
      const state = context(host);
      state.version++;
      state.pending = false;
      state.failed = false;
      message(state, '');
    });
    cancelAnimationFrame(frame);
    frame = 0;
    syncAll();
  }

  async function buy(host) {
    const state = sync(host);
    const { button, form } = state;
    if (!button || button.disabled || !form || !form.reportValidity()) return;
    message(state, '');
    const data = new FormData(form);
    const quantity = data.get('quantity');
    if (!data.get('id')) {
      message(state, host.dataset.unavailable);
      return;
    }
    if (quantity !== null && (!/^\d+$/.test(String(quantity)) || Number(quantity) < 1)) {
      message(state, host.dataset.quantityError);
      return;
    }
    const request = { state, form, button, controller: new AbortController(), timeout: null };
    state.request = request;
    active.add(request);
    sync(host);
    request.timeout = setTimeout(() => request.controller.abort(), 15000);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST', body: data, credentials: 'same-origin',
        headers: { Accept: 'application/json' }, signal: request.controller.signal,
      });
      const result = await response.json().catch(() => null);
      const current = context(host);
      if (!host.isConnected || current.request !== request) return;
      if (!result) throw new Error(host.dataset.responseError);
      if (!response.ok) throw new Error(result.description || result.message || host.dataset.purchaseError);
      window.location.assign(root + 'checkout');
    } catch (error) {
      if (!host.isConnected || state.request !== request) return;
      const text = error.name === 'AbortError' ? host.dataset.timeoutError
        : error instanceof TypeError ? host.dataset.networkError : error.message;
      cancel(state);
      message(state, text || host.dataset.purchaseError);
      sync(host);
    } finally {
      clearTimeout(request.timeout);
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('merchant-buy-now .merchant-buy-button');
    if (!button) return;
    const host = button.closest('merchant-buy-now');
    event.preventDefault();
    event.stopPropagation();
    buy(host).catch(() => {
      const state = context(host);
      cancel(state);
      message(state, host.dataset.purchaseError);
      sync(host);
    });
  }, true);

  document.addEventListener('shopify:product:select', (event) => {
    if (!event.promise || event.target.closest?.('product-card')) return;
    document.querySelectorAll('merchant-buy-now').forEach((host) => {
      const state = context(host);
      if (!state.scope?.contains(event.target)) return;
      const version = ++state.version;
      state.pending = true;
      state.failed = false;
      sync(host);
      const finish = (failed) => queueMicrotask(() => {
        if (!host.isConnected || version !== state.version) return;
        state.pending = false;
        state.failed = failed;
        message(state, failed ? host.dataset.optionError : '');
        sync(host);
      });
      Promise.resolve(event.promise).then(() => finish(false), () => finish(true));
    });
  }, true);

  document.addEventListener('change', schedule);
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:block:select', schedule);
  window.addEventListener('pagehide', () => {
    for (const request of active) cancel(request.state);
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) restore();
    else schedule();
  });
  window.addEventListener('popstate', restore);
  const observer = new MutationObserver(schedule);
  observer.observe(document.documentElement, {
    subtree: true, childList: true, attributes: true,
    attributeFilter: ['disabled', 'value', 'data-label'],
  });
  schedule();
})();
